import React, { useState } from 'react'
import GlassPanel from '../UI/GlassPanel'

/**
 * Artists you saw for the first time, by year.
 *
 * "New to you at the time" is derived from event dates on the server, so this
 * needs no data entry and stays correct when an older event is logged later.
 * The star writes back to the same `favorite` flag the event page uses.
 */
const DiscoveriesSection = ({ discoveries = [], onToggleLoved }) => {
  const [openYear, setOpenYear] = useState(discoveries[0]?.year ?? null)
  const [pending, setPending] = useState(null)

  if (discoveries.length === 0) {
    return (
      <GlassPanel className="p-6 mb-8">
        <h2 className="text-xl font-bold text-white mb-2">✨ Discoveries</h2>
        <p className="text-gray-400 text-sm">
          Mark artists as seen on your past events and the ones you saw for the
          first time will show up here, grouped by year.
        </p>
      </GlassPanel>
    )
  }

  const handleToggle = async (artist) => {
    setPending(artist.name)
    try {
      await onToggleLoved(artist)
    } finally {
      setPending(null)
    }
  }

  return (
    <GlassPanel className="p-6 mb-8">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-xl font-bold text-white">✨ Discoveries</h2>
        <p className="text-sm text-gray-400">
          Artists you saw for the first time · ★ marks the ones you loved
        </p>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {discoveries.map(group => (
          <button
            key={group.year}
            type="button"
            onClick={() => setOpenYear(group.year === openYear ? null : group.year)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              openYear === group.year
                ? 'bg-purple-600 text-white'
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            {group.year}
            <span className="ml-2 text-xs opacity-80">{group.count}</span>
          </button>
        ))}
      </div>

      {discoveries
        .filter(group => group.year === openYear)
        .map(group => (
          <div key={group.year} className="grid gap-2 sm:grid-cols-2">
            {group.artists.map(artist => (
              <div
                key={`${artist.name}-${artist.eventId}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2 transition-colors ${
                  artist.loved
                    ? 'bg-yellow-500/10 border-yellow-500/40'
                    : 'bg-white/5 border-white/15'
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleToggle(artist)}
                  disabled={pending === artist.name}
                  aria-pressed={artist.loved}
                  aria-label={`${artist.loved ? 'Unmark' : 'Mark'} ${artist.name} as loved`}
                  className={`text-lg leading-none transition-transform hover:scale-110 disabled:opacity-50 ${
                    artist.loved ? 'text-yellow-400' : 'text-gray-500 hover:text-yellow-300'
                  }`}
                >
                  {artist.loved ? '★' : '☆'}
                </button>

                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate">{artist.name}</div>
                  <div className="text-xs text-gray-400 truncate">
                    {artist.eventName}
                    {artist.venueName ? ` · ${artist.venueName}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
    </GlassPanel>
  )
}

export default DiscoveriesSection
